import React from 'react';
import { motion } from 'framer-motion';
import { HiFolder, HiExternalLink } from 'react-icons/hi';

const projects = [
  {
    title: 'Noosh Food',
    techStack: ['Shopify', 'E-commerce', 'Online Ordering'],
    description:
      'E-commerce website for a USA-based restaurant built on Shopify, facilitating seamless online ordering and delivery services.',
    link: '#',
    gradient: 'from-navy-500 to-navy-700',
  },
  {
    title: 'Material Rates Mobile App',
    techStack: ['React Native', 'Mobile', 'Real-time Data'],
    description:
      'React Native application providing daily material price updates, helping users compare product quality and prices in real-time.',
    link: '#',
    gradient: 'from-beige-500 to-beige-700',
  },
  {
    title: 'Institute MIS',
    techStack: ['Node.js', 'MongoDB', 'Full Stack'],
    description:
      'Student and staff management system streamlining administrative processes and data management for educational institutions.',
    link: '#',
    gradient: 'from-charcoal-600 to-charcoal-800',
  },
  {
    title: 'MIS for AFGAMESTAN',
    techStack: ['CodeIgniter', 'MySQL', 'Backend'],
    description:
      'Management system for employees and budgets, streamlining organizational processes and data management.',
    link: '#',
    gradient: 'from-navy-600 to-navy-800',
  },
  {
    title: 'Seda Mobile App',
    techStack: ['Flutter', 'UN Women', 'Training'],
    description:
      'Led training for the Seda mobile application project in partnership with Code to Inspire and UN Women.',
    link: '#',
    gradient: 'from-beige-600 to-beige-800',
  },
  {
    title: 'Cyber Threats',
    techStack: ['HTML', 'CSS', 'JavaScript'],
    description:
      'Educational cybersecurity website designed to raise awareness about cyber threats and promote digital safety best practices.',
    link: '#',
    gradient: 'from-navy-500 to-charcoal-700',
  },
];

const Projects = () => {
  return (
    <section id="projects" className="py-12 sm:py-16 md:py-20 bg-beige-50 relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-1/4 w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 bg-navy-400 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 bg-beige-400 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-20 relative z-10">
        <motion.div
          className="text-center mb-10 sm:mb-12 md:mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-beige-600 font-semibold text-xs sm:text-sm uppercase tracking-wider">Portfolio</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-navy-900 mt-2 font-primary">
            Featured Projects
          </h2>
          <div className="w-16 sm:w-20 h-1 bg-beige-500 mx-auto mt-3 sm:mt-4"></div>
          <p className="mt-4 sm:mt-6 text-gray-600 max-w-2xl mx-auto text-sm sm:text-base px-4">
            A selection of projects showcasing my experience in web development, mobile applications,
            and full-stack solutions.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-7xl mx-auto">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-2xl hover:border-beige-400/50 transition-all duration-300"
            >
              <div className={`h-1.5 sm:h-2 bg-gradient-to-r ${project.gradient}`}></div>

              <div className="p-4 sm:p-6">
                {/* Icon and Title */}
                <div className="flex items-start justify-between mb-3 sm:mb-4">
                  <div className="p-2 sm:p-3 bg-beige-100 rounded-lg sm:rounded-xl group-hover:bg-beige-200 transition-colors">
                    <HiFolder className="text-xl sm:text-2xl text-beige-700" />
                  </div>
                  <a
                    href={project.link}
                    className="p-1.5 sm:p-2 text-gray-400 hover:text-navy-700 transition-colors"
                    aria-label="View project"
                  >
                    <HiExternalLink className="text-lg sm:text-xl" />
                  </a>
                </div>

                <h3 className="text-base sm:text-lg md:text-xl font-bold text-navy-900 mb-2 sm:mb-3 font-primary group-hover:text-navy-700 transition-colors">
                  {project.title}
                </h3>

                <p className="text-gray-600 leading-relaxed mb-3 sm:mb-4 text-xs sm:text-sm">
                  {project.description}
                </p>

                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {project.techStack.map((tech, techIndex) => (
                    <span
                      key={techIndex}
                      className="px-2 py-0.5 sm:px-3 sm:py-1 bg-navy-50 text-navy-700 rounded-md sm:rounded-lg text-xs font-medium border border-navy-100"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
